import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "guest", label: "Guest" },
  { value: "agent", label: "Agent" },
];

const initialFormData = {
  username: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  role: "user",
  is_active: true,
};

const CreateUserModal = ({ open, onClose, onCreateUser }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setErrors({});
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await onCreateUser(formData);
    setIsSubmitting(false);

    if (result.success) {
      onClose();
    } else if (result.errors) {
      // Map backend errors to form fields
      const backendErrors = {};
      Object.entries(result.errors).forEach(([key, value]) => {
        backendErrors[key] = Array.isArray(value) ? value[0] : value;
      });
      setErrors(backendErrors);
    }
  };

  return (
    <Modal
      aria-labelledby="create-user-modal-title"
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: "md",
          p: 3,
          boxShadow: "lg",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.surface",
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={onClose} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <PersonAddIcon color="primary" />
          <Typography
            component="h2"
            id="create-user-modal-title"
            level="h4"
            textColor="inherit"
            sx={{ fontWeight: "lg" }}
          >
            Create New User
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormControl error={!!errors.username} required>
            <FormLabel>Username</FormLabel>
            <Input
              placeholder="Enter username"
              value={formData.username}
              onChange={handleChange("username")}
            />
            {errors.username && (
              <Typography level="body-xs" color="danger">
                {errors.username}
              </Typography>
            )}
          </FormControl>

          <FormControl error={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="Enter email (optional)"
              value={formData.email}
              onChange={handleChange("email")}
            />
            {errors.email && (
              <Typography level="body-xs" color="danger">
                {errors.email}
              </Typography>
            )}
          </FormControl>

          <FormControl error={!!errors.password} required>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              placeholder="Enter password"
              value={formData.password}
              onChange={handleChange("password")}
            />
            {errors.password && (
              <Typography level="body-xs" color="danger">
                {errors.password}
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl error={!!errors.first_name} required sx={{ flex: 1 }}>
              <FormLabel>First Name</FormLabel>
              <Input
                placeholder="First name"
                value={formData.first_name}
                onChange={handleChange("first_name")}
              />
              {errors.first_name && (
                <Typography level="body-xs" color="danger">
                  {errors.first_name}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.last_name} required sx={{ flex: 1 }}>
              <FormLabel>Last Name</FormLabel>
              <Input
                placeholder="Last name"
                value={formData.last_name}
                onChange={handleChange("last_name")}
              />
              {errors.last_name && (
                <Typography level="body-xs" color="danger">
                  {errors.last_name}
                </Typography>
              )}
            </FormControl>
          </Box>

          <FormControl error={!!errors.role}>
            <FormLabel>Role</FormLabel>
            <Select
              value={formData.role}
              onChange={(_, value) => handleChange("role")(value)}
            >
              {ROLES.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
            {errors.role && (
              <Typography level="body-xs" color="danger">
                {errors.role}
              </Typography>
            )}
          </FormControl>

          <FormControl
            orientation="horizontal"
            sx={{ justifyContent: "space-between" }}
          >
            <Box>
              <FormLabel>Active</FormLabel>
              <Typography level="body-xs" color="neutral">
                User can log in and access the system
              </Typography>
            </Box>
            <Switch
              checked={formData.is_active}
              onChange={handleSwitchChange("is_active")}
              color={formData.is_active ? "success" : "neutral"}
            />
          </FormControl>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={onClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="solid"
            color="primary"
            loading={isSubmitting}
          >
            Create User
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
};

export default CreateUserModal;
